import { useEffect, useRef, useState } from "react";
import { Bot, Check, Loader2, Send, Sparkles } from "@fg/icons";
import { Button } from "@/components/ui/button";
import { useAiChat } from "@/api/hooks";
import type { AiChatRequest } from "@/api/mailApi";
import { sanitizeHtml } from "@/lib/sanitizeHtml";

type Msg = { role: "user" | "assistant"; content: string };

/**
 * Reusable, ITERATIVE AI text generator (a mini-chat). Used for the AI signature
 * and the out-of-office auto-reply. The user gives an initial description, gets a
 * preview, then refines it conversationally ("make it shorter", "add my phone")
 * until happy, and applies the result. Each turn keeps the full conversation so
 * the model edits its own previous output.
 */
export function AiTextGenerator({
    mode,
    buttonLabel = "Generate with AI",
    title,
    firstPlaceholder,
    refinePlaceholder = "Refine it — e.g. “make it shorter”, “add my phone”, “more formal”",
    buildInitial,
    onApply
}: {
    mode: AiChatRequest["mode"];
    buttonLabel?: string;
    title: string;
    firstPlaceholder: string;
    refinePlaceholder?: string;
    /** Build the first user message from the caller's context + the typed input. */
    buildInitial: (firstInput: string) => string;
    onApply: (html: string) => void;
}) {
    const ai = useAiChat();
    const [open, setOpen] = useState(false);
    const [msgs, setMsgs] = useState<Msg[]>([]);
    const [input, setInput] = useState("");
    const [preview, setPreview] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
        window.addEventListener("mousedown", onClick);
        return () => window.removeEventListener("mousedown", onClick);
    }, [open]);

    const reset = () => { setMsgs([]); setInput(""); setPreview(null); setError(null); };

    const send = async () => {
        if (ai.isPending) return;
        const isFirst = msgs.length === 0;
        const text = input.trim();
        if (!isFirst && !text) return;
        setError(null);
        const userContent = isFirst ? buildInitial(text) : text;
        if (!userContent.trim()) { setError("Add a few details first."); return; }
        const next: Msg[] = [...msgs, { role: "user", content: userContent }];
        setMsgs(next);
        setInput("");
        try {
            const resp = await ai.mutateAsync({ mode, messages: next });
            const html = resp.composeSuggestion?.bodyHtml
                ?? (resp.reply?.trim() ? `<p>${resp.reply.trim().replace(/\n+/g, "<br/>")}</p>` : null);
            if (html) {
                setPreview(html);
                // Keep the model's raw reply in history so the next refinement edits it.
                setMsgs((m) => [...m, { role: "assistant", content: resp.reply || html }]);
            } else {
                setError("Nothing was generated. Try again.");
            }
        } catch (e) {
            setError(e instanceof Error ? e.message : "Generation failed.");
        }
    };

    return (
        <div ref={ref} className="relative">
            <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => { setOpen((v) => !v); }}
                title={title}
            >
                <Bot className="h-3.5 w-3.5" /> {buttonLabel}
            </Button>
            {open ? (
                <div className="absolute right-0 top-full z-20 mt-1 w-96 rounded-lg border bg-card p-3 shadow-lg">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
                            <Sparkles className="h-3.5 w-3.5" /> {title}
                        </div>
                        {msgs.length > 0 ? (
                            <button type="button" onClick={reset} className="text-[11px] text-muted-foreground hover:text-foreground">
                                Start over
                            </button>
                        ) : null}
                    </div>

                    {preview ? (
                        <div className="mb-2 space-y-1.5">
                            <div
                                className="max-h-40 overflow-y-auto rounded border bg-background p-2 text-xs text-foreground/80 [&_p]:mb-1 [&_p:last-child]:mb-0"
                                dangerouslySetInnerHTML={{ __html: sanitizeHtml(preview) }}
                            />
                            <Button size="sm" className="h-7 w-full text-xs" onClick={() => { onApply(preview); setOpen(false); }}>
                                <Check className="mr-1 h-3.5 w-3.5" /> Use this
                            </Button>
                        </div>
                    ) : null}

                    <div className="flex items-end gap-1.5">
                        <textarea
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            rows={2}
                            placeholder={preview ? refinePlaceholder : firstPlaceholder}
                            disabled={ai.isPending}
                            className="min-w-0 flex-1 resize-none rounded-md border px-2 py-1.5 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
                            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
                        />
                        <Button size="sm" className="h-9 shrink-0 px-3" disabled={ai.isPending} onClick={() => void send()}>
                            {ai.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                        </Button>
                    </div>
                    {error ? <p className="mt-1.5 text-xs text-destructive">{error}</p> : null}
                    {preview ? (
                        <p className="mt-1.5 text-[10px] text-muted-foreground">Keep typing to refine, or "Use this" to apply.</p>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
